import { Modal } from 'react-bootstrap';
import { StockLine } from '../models/StockLine';
import { useIntl } from 'react-intl';
import { SaleableProductModel } from '../../b2b-orders';
import { StockLog } from '../models/StockLog';
import { useEffect, useState } from 'react';
import { listStockLogs } from '../services/StockLinesCRUD';
import { toast } from 'react-toastify';
import getWarehouseOptions from '../services/getWarehouseOptions';
import getKindOptions from '../services/getKindOptions';
import getReasonOptions from '../services/getReasonOptions';
import { Link } from 'react-router-dom';

interface StockModalProps {
  stockLine?: StockLine;
  onClose: () => void;
  visible?: boolean;
  products?: Array<SaleableProductModel>;
}

export default function StockModal({
  stockLine = null,
  onClose,
  visible = false,
  products = [],
}: StockModalProps) {
  const intl = useIntl();
  const [logs, setLogs] = useState<Array<StockLog>>([]);
  const reasonOptions = getReasonOptions(intl);

  async function fetchLogs() {
    try {
      const { data } = await listStockLogs({
        kind: stockLine?.kind,
        warehouse: stockLine?.warehouse,
        product: stockLine?.product,
      });
      setLogs(data);
    } catch (error) {
      console.error('Error fetching stock logs:', error);
      toast.error(intl.formatMessage({ id: 'stock.errorFetchingLogs' }));
    }
  }

  useEffect(() => {
    if (visible) {
      fetchLogs();
    }
  }, [stockLine, visible]);

  return (
    <Modal
      show={visible}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({ id: 'stock.stockLogs' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className="d-flex flex-column gap-5">
          {!logs.length && <p>{intl.formatMessage({ id: 'stock.noLogs' })}</p>}
          {logs?.length > 0 && (
            <table className="table table-striped table-hover">
              <thead className="fw-bolder">
                <tr>
                  <th>{intl.formatMessage({ id: 'stock.product' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.warehouse' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.kind' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.quantity' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.reason' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.date' })}</th>
                  <th>{intl.formatMessage({ id: 'stock.user' })}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      {
                        products.find((product) => product.id === log.product)
                          ?.name
                      }
                    </td>
                    <td>
                      {
                        getWarehouseOptions(intl).find(
                          (warehouse) => warehouse.value === log.warehouse
                        )?.label
                      }
                    </td>
                    <td>
                      {
                        getKindOptions(intl).find(
                          (kind) => kind.value === log.kind
                        )?.label
                      }
                    </td>
                    <td>{log.quantity}</td>
                    <td>
                      {reasonOptions.find(
                        (reason) => reason.value === log.reason
                      )?.label || '-'}
                    </td>
                    <td>
                      {new Date(log.performed_at).toLocaleString(intl.locale, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                    </td>
                    <td>
                      <Link to={`/user/${log.performed_by?.id}/profile`}>
                        {log.performed_by?.email || '-'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-text" onClick={onClose}>
          {intl.formatMessage({ id: 'stock.close' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
